import React, { useState, useRef, useEffect } from 'react';
import { FiSmile, FiX, FiSearch, FiCheck, FiChevronDown } from 'react-icons/fi';
import DynamicIcon, { AVAILABLE_REACT_ICONS, POPULAR_EMOJIS, ICON_MAP } from '../../../components/shared/DynamicIcon';

const IconEmojiPicker = ({
    value = '',
    onChange,
    label = 'Icon / Emoji / Huy hiệu',
    placeholder = "VD: FiZap, ⚡, 🛡️, hoặc 'TỐI ƯU'"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('react-icons'); // 'react-icons' | 'emojis'
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('ALL');
    const containerRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const categories = ['ALL', 'Tech', 'Security', 'Medical', 'Business', 'General'];

    const filteredReactIcons = AVAILABLE_REACT_ICONS.filter((item) => {
        const matchesCat = categoryFilter === 'ALL' || item.category === categoryFilter;
        const q = searchQuery.toLowerCase();
        const matchesQuery = !q || item.name.toLowerCase().includes(q) || item.label.toLowerCase().includes(q);
        return matchesCat && matchesQuery;
    });

    const filteredEmojis = POPULAR_EMOJIS.filter((emoji) => {
        return !searchQuery || emoji.includes(searchQuery);
    });

    const handleSelectIcon = (iconName) => {
        if (onChange) onChange(iconName);
        setIsOpen(false);
    };

    const handleSelectEmoji = (emoji) => {
        if (onChange) onChange(emoji);
        setIsOpen(false);
    };

    return (
        <div className={`ps-icon-picker-container ${isOpen ? 'is-open' : ''}`} ref={containerRef}>
            {label && <label className="form-label">{label}</label>}

            <div className="ps-icon-input-group">
                {/* Visual Preview Box */}
                <div
                    className="ps-icon-preview-box"
                    onClick={() => setIsOpen(!isOpen)}
                    title="Nhấn để chọn Icon / Emoji"
                >
                    {value ? (
                        <DynamicIcon icon={value} size={20} />
                    ) : (
                        <FiSmile size={18} className="ps-icon-placeholder-icon" />
                    )}
                </div>

                {/* Text input for manual type or chosen icon name */}
                <input
                    type="text"
                    className="form-control ps-icon-text-input"
                    placeholder={placeholder}
                    value={value || ''}
                    onChange={(e) => onChange && onChange(e.target.value)}
                />

                {/* Clear button */}
                {value && (
                    <button
                        type="button"
                        className="ps-icon-clear-btn"
                        onClick={() => onChange && onChange('')}
                        title="Xóa icon"
                    >
                        <FiX size={15} />
                    </button>
                )}

                {/* Trigger Button */}
                <button
                    type="button"
                    className={`ps-icon-trigger-btn ${isOpen ? 'active' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                    title="Mở bảng chọn React Icon & Emoji"
                >
                    <span>Chọn icon</span>
                    <FiChevronDown size={14} className={`chevron-icon ${isOpen ? 'open' : ''}`} />
                </button>
            </div>

            {/* Picker Popup Dropdown */}
            {isOpen && (
                <div className="ps-icon-picker-dropdown">
                    {/* Header Tabs */}
                    <div className="ps-icon-picker-tabs">
                        <button
                            type="button"
                            className={`picker-tab-btn ${activeTab === 'react-icons' ? 'active' : ''}`}
                            onClick={() => setActiveTab('react-icons')}
                        >
                            <span>⚛️ React Icons</span>
                            <span className="tab-count">{AVAILABLE_REACT_ICONS.length}</span>
                        </button>
                        <button
                            type="button"
                            className={`picker-tab-btn ${activeTab === 'emojis' ? 'active' : ''}`}
                            onClick={() => setActiveTab('emojis')}
                        >
                            <span>😀 Emojis</span>
                            <span className="tab-count">{POPULAR_EMOJIS.length}</span>
                        </button>
                    </div>

                    {/* Search bar */}
                    <div className="ps-icon-search-bar">
                        <FiSearch className="search-icon" size={14} />
                        <input
                            type="text"
                            placeholder="Tìm kiếm icon (VD: shield, zap, tim, cloud)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="search-clear"
                                onClick={() => setSearchQuery('')}
                            >
                                <FiX size={12} />
                            </button>
                        )}
                    </div>

                    {/* Category filter pills for React Icons */}
                    {activeTab === 'react-icons' && (
                        <div className="ps-icon-categories">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    type="button"
                                    className={`cat-pill ${categoryFilter === cat ? 'active' : ''}`}
                                    onClick={() => setCategoryFilter(cat)}
                                >
                                    {cat === 'ALL' ? 'Tất cả' : cat}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Content Grid */}
                    <div className="ps-icon-grid-scroll">
                        {activeTab === 'react-icons' ? (
                            filteredReactIcons.length > 0 ? (
                                <div className="ps-react-icons-grid">
                                    {filteredReactIcons.map((item) => {
                                        const isSelected = value === item.name;
                                        return (
                                            <button
                                                key={item.name}
                                                type="button"
                                                className={`ps-icon-card ${isSelected ? 'selected' : ''}`}
                                                onClick={() => handleSelectIcon(item.name)}
                                                title={`${item.name} - ${item.label}`}
                                            >
                                                <div className="icon-wrapper">
                                                    <DynamicIcon icon={item.name} size={22} />
                                                </div>
                                                <span className="icon-name">{item.name}</span>
                                                <span className="icon-label-sub">{item.label}</span>
                                                {isSelected && <FiCheck className="selected-check" size={12} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="no-icons-found">Không tìm thấy icon phù hợp</p>
                            )
                        ) : (
                            <div className="ps-emojis-grid">
                                {filteredEmojis.map((emoji) => {
                                    const isSelected = value === emoji;
                                    return (
                                        <button
                                            key={emoji}
                                            type="button"
                                            className={`ps-emoji-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleSelectEmoji(emoji)}
                                        >
                                            <span className="emoji-char">{emoji}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default IconEmojiPicker;
